import {GiftCardErrorCode, getEventId} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Gift} from '@wix/wix-ui-icons-common'
import {ButtonPriority, StatesButtonStates} from '@wix/wix-ui-tpa'
import {
  HoverStyles,
  StatesButton,
  Text,
  TextButton,
  TextButtonPriority,
  TextField,
  ThreeDotsLoader,
} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useRef, useState} from 'react'
import {FormStep} from '../../constants'
import {useAppActions, useAppSelector} from '../../hooks/runtime'
import {getEvent} from '../../selectors/event'
import {
  getGiftCardError,
  getGiftCardPaymentDetails,
  giftCardSubmitted,
  isValidatingCoupon,
  isValidatingGiftCard,
} from '../../selectors/invoice'
import {getReservationId} from '../../selectors/reservation'
import {classes} from './gift-card-input.st.css'
import {GiftCardFormState, GiftCardInputProps} from './interfaces'

export const GiftCardInput = ({currentStep, mobile = false}: GiftCardInputProps) => {
  const {t} = useTranslation()
  const {editStep, applyGiftCard, removeGiftCard} = useAppActions()
  const [state, setState] = useState(GiftCardFormState.INITIAL)
  const [giftCardCode, setGiftCardCode] = useState('')
  const iconRef = useRef(null)

  const eventId = getEventId(useAppSelector(getEvent))
  const reservationId = useAppSelector(getReservationId)
  const giftCardPaymentDetails = useAppSelector(getGiftCardPaymentDetails)
  const validatingCoupon = useAppSelector(isValidatingCoupon)
  const validating = useAppSelector(isValidatingGiftCard)
  const submitted = useAppSelector(giftCardSubmitted)
  const error = useAppSelector(getGiftCardError)

  const paymentStep = currentStep === FormStep.Payment
  const submitButtonState = validating
    ? StatesButtonStates.IN_PROGRESS
    : submitted
      ? StatesButtonStates.SUCCESS
      : StatesButtonStates.IDLE
  const errorMessage = !error
    ? null
    : error === GiftCardErrorCode.EVENTS_GIFT_CARD_NOT_FOUND
      ? t('summary.giftCardError.notFound')
      : error === GiftCardErrorCode.EVENTS_GIFT_CARD_INSUFFICIENT_FUNDS
        ? t('summary.giftCardError.insufficientFunds')
        : error === GiftCardErrorCode.EVENTS_GIFT_CARD_DISABLED
          ? t('summary.giftCardError.disabled')
          : error === GiftCardErrorCode.EVENTS_GIFT_CARD_EXPIRED
            ? t('summary.giftCardError.expired')
            : t('summary.giftCardError.unknown')

  useEffect(() => {
    if (submitted && !validating) {
      setTimeout(() => {
        setState(GiftCardFormState.APPLIED)
      }, 1000)
    }
  }, [submitted, validating])

  useEffect(() => {
    if (state === GiftCardFormState.APPLIED && iconRef.current) {
      iconRef.current.focus()
    }
  }, [state])

  useEffect(() => {
    if (validatingCoupon && !submitted) {
      setState(GiftCardFormState.INITIAL)
      setGiftCardCode('')
    }
  }, [validatingCoupon])

  const submit = () => {
    if (validating || submitted) {
      return
    }

    if (paymentStep) {
      editStep({step: FormStep.BuyerDetails, validPaymentAdded: false})
    }

    applyGiftCard(eventId, reservationId, giftCardCode.trim())
  }

  const clear = () => {
    if (validating || submitted) {
      return
    }

    setGiftCardCode('')
    removeGiftCard()
  }

  const remove = () => {
    if (paymentStep) {
      editStep({step: FormStep.BuyerDetails, validPaymentAdded: false})
    }

    removeGiftCard()
    setState(GiftCardFormState.EDIT)
    setGiftCardCode('')
  }

  const showEditView = () => setState(GiftCardFormState.EDIT)

  return (
    <div className={classNames(classes.container, {[classes.mobile]: mobile})}>
      {state === GiftCardFormState.INITIAL && (
        <TextButton
          className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
          onClick={showEditView}
          prefixIcon={<Gift />}
          data-hook={DH.ADD_GIFT_CARD_BUTTON}
          priority={TextButtonPriority.link}
          hoverStyle={HoverStyles.underline}
        >
          {t('giftCardCodeInput_addGiftCardCodeCta')}
        </TextButton>
      )}
      {state === GiftCardFormState.EDIT && (
        <div aria-live="polite" className={classes.applying}>
          <TextField
            label={t('giftCardCodeInput_label')}
            placeholder={t('giftCardCodeInput_placeholder')}
            withClearButton
            error={!!error}
            errorMessage={errorMessage}
            onChange={e => setGiftCardCode(e.target.value)}
            readOnly={validating || submitted}
            value={giftCardCode}
            newErrorMessage
            // @ts-expect-error
            errorAppearance="BackgroundAndBorder"
            onClear={clear}
            className={classes.giftCardInput}
            aria-label={t('a11y.giftCardCodeInputField')}
            maxLength={20}
            data-hook={DH.GIFT_CARD_CODE_INPUT}
            clearButtonAriaLabel={t('a11y.clearGiftCardCodeButton')}
            color="red"
          />
          <StatesButton
            aria-label={validating || submitted ? t('a11y.giftCardApplying') : undefined}
            state={submitButtonState}
            idleContent={t('giftCardCodeInput_applyButton')}
            inProgressContent={<ThreeDotsLoader className={classes.loader} />}
            onClick={submit}
            priority={ButtonPriority.basicSecondary}
            className={classes.applyGiftCardButton}
            data-hook={DH.GIFT_CARD_CODE_SUBMIT}
            upgrade
            fullWidth
          />
        </div>
      )}
      {state === GiftCardFormState.APPLIED && giftCardPaymentDetails?.[0] && (
        <div className={classes.applied} data-hook={DH.GIFT_CARD_APPLIED}>
          <div className={classes.giftCard}>
            <div ref={iconRef} tabIndex={0} aria-label={t('a11y.giftCardApplied')} role="img" className={classes.gift}>
              <Gift />
            </div>
            <Text className={classNames(classes.giftCardCode, {[classes.desktopFont]: !mobile})}>
              {giftCardPaymentDetails[0].obfuscatedCode}
            </Text>
          </div>
          <TextButton
            onClick={remove}
            className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
            data-hook={DH.GIFT_CARD_CODE_CLEAR}
            aria-label={t('a11y.clearGiftCardCodeButton')}
            priority={TextButtonPriority.link}
            hoverStyle={HoverStyles.underline}
          >
            {t('giftCardCodeInput_removeButton')}
          </TextButton>
        </div>
      )}
    </div>
  )
}
